/**
 * A conferência da leitura na tela (E140.F3, ADR-0096 §2.7): lê ao montar e a cada `releitura` (o
 * caso relido depois do exame), relê sozinha enquanto o sistema recupera a leitura (`corrigir`) ou
 * depois que a equipe pede para ler de novo (a leitura roda em segundo plano), e aplica o pedido da
 * equipe, trocando a conferência pela refeita que o servidor devolve.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  ConferenciaDaLeituraError,
  PedidoDaEquipeError,
  emRecuperacao,
  lerConferenciaDaLeitura,
  pedirPelaEquipe,
  type ConferenciaDaLeituraOutData,
  type MotivoDaFalhaDaConferencia,
  type PedidoDeRetomadaData,
  type RecusaDoPedido,
} from '@/lib/conferencia_da_leitura';

export const INTERVALO_DA_RELEITURA_MS = 20_000;
export const RELEITURAS_MAXIMAS = 9;

export type EstadoDaConferenciaNaTela =
  | { estado: 'carregando' }
  | { estado: 'vazio' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDaConferencia }
  | { estado: 'pronto'; dados: ConferenciaDaLeituraOutData };

export type ResultadoDoPedido = { ok: true } | { ok: false; recusa: RecusaDoPedido };

export interface UsoDaConferencia {
  conferencia: EstadoDaConferenciaNaTela;
  reler: () => void;
  pedir: (pedido: PedidoDeRetomadaData) => Promise<ResultadoDoPedido>;
}

export function useConferenciaDaLeitura(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): UsoDaConferencia {
  const [conferencia, setConferencia] = useState<EstadoDaConferenciaNaTela>({
    estado: 'carregando',
  });
  const [pedido, setPedido] = useState(0);
  const relidas = useRef(0);
  const aguardando = useRef(false);
  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    relidas.current = 0;
  }, [caseId, releitura]);

  useEffect(() => {
    let vivo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      try {
        const dados = await lerConferenciaDaLeitura(client, caseId);
        if (!vivo) return;
        setConferencia(dados === null ? { estado: 'vazio' } : { estado: 'pronto', dados });
        const relendo = emRecuperacao(dados) || aguardando.current;
        if (relendo && relidas.current < RELEITURAS_MAXIMAS) {
          relidas.current += 1;
          espera = setTimeout(reler, INTERVALO_DA_RELEITURA_MS);
        } else {
          aguardando.current = false;
        }
      } catch (err) {
        if (!vivo) return;
        const motivo = err instanceof ConferenciaDaLeituraError ? err.motivo : 'indisponivel';
        setConferencia({ estado: 'erro', motivo });
      }
    })();
    return () => {
      vivo = false;
      if (espera !== undefined) clearTimeout(espera);
    };
  }, [client, caseId, releitura, pedido, reler]);

  const pedir = useCallback(
    async (corpo: PedidoDeRetomadaData): Promise<ResultadoDoPedido> => {
      try {
        const dados = await pedirPelaEquipe(client, caseId, corpo);
        setConferencia({ estado: 'pronto', dados });
        if (corpo.acao === 'tentativa') {
          aguardando.current = true;
          relidas.current = 0;
          reler();
        }
        return { ok: true };
      } catch (err) {
        const recusa = err instanceof PedidoDaEquipeError ? err.recusa : 'indisponivel';
        return { ok: false, recusa };
      }
    },
    [client, caseId, reler],
  );

  return { conferencia, reler, pedir };
}
